import React, { useState } from "react";
import {
  OmniDraftQuery,
  useOmniDeleteDraftMutation,
  useOmniCommitManualEditMutation,
  useOmniUpdateDraftMetaMutation,
  useOmniApproveDraftMutation,
  useOmniReopenDraftMutation,
  useOmniChannelsQuery,
  useOmniWorkflowTypesQuery,
  useOmniCreatePlanMutation,
  useOmniRequestPlanRenderingMutation,
  useOmniPostLazyQuery
} from "../../../lib/apollo/graphql/omni/omni-generated";
import { ArrowLeftIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { formatDate } from "../../../utilities/format";
import Editor from "../../common/components/Editor";
import { useNavigate } from "react-router-dom";

type DraftDetail = OmniDraftQuery['draft'];

interface DraftVersion {
  id: string;
  name: string;
  content: string;
  confirmedAt?: string;
  isLatest: boolean;
  versionNo?: number;
}

interface DraftDetailPageProps {
  draft: DraftDetail | null;
  onClose: () => void;
  onSave?: (draft: DraftDetail) => void;
  onRefreshDraft?: () => void;
  refetchDraft?: () => Promise<any>;
}



const STATUS_COLORS = {
  APPROVED: "bg-green-100 text-green-800 border border-green-200",
  DELETED: "bg-red-100 text-red-800 border border-red-200",
  EDITING: "bg-blue-100 text-blue-800 border border-blue-200",
  NEW: "bg-gray-100 text-gray-800 border border-gray-200",
};

const STATUS_LABELS = {
  APPROVED: "승인",
  DELETED: "(삭제)",
  EDITING: "검토중",
  NEW: "초안",
};


export default function DraftDetailPage({
  draft,
  onClose,
  onSave,
  onRefreshDraft,
  refetchDraft,
}: DraftDetailPageProps) {
  const navigate = useNavigate();
  const [deleteDraft] = useOmniDeleteDraftMutation();
  const [commitManualEdit] = useOmniCommitManualEditMutation();
  const [updateDraftMeta] = useOmniUpdateDraftMetaMutation();
  const [approveDraft] = useOmniApproveDraftMutation();
  const [reopenDraft] = useOmniReopenDraftMutation();
  const [getPost] = useOmniPostLazyQuery();

  // 게시글 존재 여부 확인 후 이동
  const handleNavigateToPost = async (postId: string) => {
    try {
      const { data } = await getPost({
        variables: { postId },
        fetchPolicy: 'network-only',
      });

      if (data?.post) {
        // 게시글이 존재하면 이동
        navigate(`/omni/post/${postId}`);
      } else {
        // 게시글이 조회되지 않으면 알림
        alert('게시글이 삭제되었습니다.');
      }
    } catch (error) {
      console.error('Failed to fetch post:', error);
      alert('게시글이 삭제되었습니다.');
    }
  };

  // 버전 내역 스크롤을 위한 ref
  const versionListRef = React.useRef<HTMLDivElement>(null);
  // artifactVersions에서 확정된 버전 데이터 생성
  const confirmedVersions = React.useMemo(() => {
    if (!draft?.artifactVersions || draft.artifactVersions.length === 0) {
      return [];
    }

    return [...draft.artifactVersions] // 읽기 전용 배열을 복사
      .sort((a, b) => a.versionNo - b.versionNo) // 버전 번호 순으로 정렬
      .map((version) => ({
        id: `v${version.versionNo}`,
        name: version.memo || `버전 ${version.versionNo}`,
        content: version.content,
        confirmedAt: version.createdAt,
        isLatest: false, // 모든 artifact 버전은 확정된 버전이므로 isLatest는 false
        versionNo: version.versionNo,
      }));
  }, [draft?.artifactVersions]);

  // 현재 편집 중인 새 버전의 이름 (사용자가 추가 버튼을 눌렀을 때만 생성)
  const [editingVersionName, setEditingVersionName] = React.useState<string>("");
  // 편집 중인 내용 (독립적으로 관리)
  const [editingContent, setEditingContent] = React.useState<string>("");
  // 편집 모드 여부
  const [isEditingMode, setIsEditingMode] = React.useState<boolean>(false);

  // 전체 버전 목록 (확정된 버전들 + 편집 중인 버전)
  const allVersions = React.useMemo(() => {
    const versions: DraftVersion[] = [...confirmedVersions];
    if (isEditingMode) {
      const editingVersion: DraftVersion = {
        id: "new",
        name: editingVersionName,
        content: editingContent,
        isLatest: true,
        // confirmedAt과 versionNo는 optional이므로 생략
      };
      versions.push(editingVersion);
    }
    return versions;
  }, [confirmedVersions, isEditingMode, editingVersionName, editingContent]);

  // 버전 내역이 변경될 때마다 맨 아래로 스크롤
  React.useEffect(() => {
    if (versionListRef.current) {
      versionListRef.current.scrollTop = versionListRef.current.scrollHeight;
    }
  }, [allVersions]);

  // 가장 최근에 확정된 버전의 날짜 찾기 (현재 미사용)
  // const getLatestConfirmedDate = () => {
  //   const confirmedVersions = versions.filter(v => v.confirmedAt);
  //   if (confirmedVersions.length === 0) return null;
  //   return confirmedVersions.reduce((latest, current) => 
  //     new Date(current.confirmedAt!) > new Date(latest.confirmedAt!) ? current : latest
  //   ).confirmedAt;
  // };

  const [selectedVersion, setSelectedVersion] = useState<DraftVersion | null>(null);
  const [isEditingVersionName, setIsEditingVersionName] = useState<boolean>(false);
  const [tempVersionName, setTempVersionName] = useState("");
  const [draftMemo, setDraftMemo] = useState<string>(draft?.memo || "");
  const [isMemoChanged, setIsMemoChanged] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isApprovingDraft, setIsApprovingDraft] = useState(false);

  // draft가 변경될 때 memo 상태 동기화
  React.useEffect(() => {
    if (draft?.memo !== undefined) {
      setDraftMemo(draft.memo || "");
      setIsMemoChanged(false);
    }
  }, [draft?.memo]);

  // 초기 버전 선택 (편집 모드가 있으면 편집 중인 버전, 없으면 가장 최근 확정된 버전)
  React.useEffect(() => {
    if (isEditingMode) {
      // 편집 모드인 경우 편집 중인 버전 선택
      const editingVersion = allVersions.find(v => v.id === "new");
      if (editingVersion) {
        setSelectedVersion(editingVersion);
      }
    } else if (confirmedVersions.length > 0) {
      // 확정된 버전이 있으면 가장 최근 버전 선택
      const latestVersion = confirmedVersions[confirmedVersions.length - 1];
      setSelectedVersion(latestVersion);
    } else {
      // 버전이 없는 경우
      setSelectedVersion(null);
    }
  }, [isEditingMode, allVersions, confirmedVersions]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // 활성 채널 조회
  const { data: channelsData } = useOmniChannelsQuery({
    variables: {
      input: { active: true }
    }
  });

  const activeChannels = channelsData?.channels?.items || [];

  // 워크플로우 타입 조회
  const { data: workflowTypesData } = useOmniWorkflowTypesQuery();
  const [createPlan] = useOmniCreatePlanMutation();
  const [requestPlanRendering] = useOmniRequestPlanRenderingMutation();
  
  // post_rendering 코드를 가진 워크플로우 타입의 ID 찾기
  const postRenderingWorkflowType = workflowTypesData?.workflowTypes?.items?.find(
    (type) => type.code === 'post_rendering'
  );
  const workflowTypeId = postRenderingWorkflowType?.id;
  
  // 채널별 게시글 상태 - plans 정보와 매칭
  const channelPosts = activeChannels.map(channel => {
    // draft의 plans에서 해당 채널의 플랜 찾기
    const channelPlan = draft?.plans?.find((plan: any) => plan.channelId === channel.id);

    return {
      id: channel.id,
      channel: channel.name,
      channelType: channel.type,
      status: channelPlan?.status || 'PENDING',
      requestedAt: channelPlan?.requestedAt || '-',
      memo: channelPlan?.statusDescription || '-',
      postId: channelPlan?.postId,
      planId: channelPlan?.planId,
    };
  });

  // 채널 게시글 중 하나라도 완료되면 삭제 불가
  const hasCompletedChannelPost = channelPosts.some(post => 
    post.status === 'COMPLETED' || post.status === 'PUBLISHED'
  );

  const handleVersionSelect = (version: DraftVersion) => {
    setSelectedVersion(version);
    // 버전 선택은 단순히 표시용이므로 편집 내용에 영향주지 않음
  };


  const handleReset = () => {
    if (isEditingMode) {
      // 편집 모드에서만 초기화 가능 - 가장 최근 확정된 버전의 내용으로 초기화
      const latestConfirmedContent = confirmedVersions.length > 0 
        ? confirmedVersions[confirmedVersions.length - 1].content 
        : draft?.contentMd || '';
      setEditingContent(latestConfirmedContent);
    }
  };

  const handleConfirmVersion = async () => {
    if (!isEditingMode || !draft) return;
    
    // 가장 최신 artifactId 가져오기
    const latestArtifactId = draft.artifactId;
    if (!latestArtifactId) {
      alert("artifactId를 찾을 수 없습니다.");
      return;
    }
    
    try {
      // 수동 편집 커밋 API 호출
      const result = await commitManualEdit({
        variables: {
          input: {
            artifactId: latestArtifactId,
            content: editingContent,
            memo: editingVersionName,
          }
        }
      });
      
      if (result.data?.commitManualEdit) {
        alert(`버전 ${result.data.commitManualEdit.versionNo}이(가) 확정되었습니다.`);
        
        // 편집 모드 종료
        setIsEditingMode(false);
        setEditingVersionName("");
        setEditingContent("");
        
        // draft 정보 다시 조회하여 새로운 artifact 버전 반영
        const refreshFunction = refetchDraft || onRefreshDraft;
        if (refreshFunction) {
          refreshFunction();
        }
      }
    } catch (error) {
      console.error("Version confirmation failed:", error);
      alert("버전 확정에 실패했습니다. 다시 시도해주세요.");
    }
  };

  // 편집 중인 버전 취소 기능
  const handleCancelEditingVersion = () => {
    if (!isEditingMode) return;
    
    if (!window.confirm('편집 중인 버전을 취소하시겠습니까? 변경사항이 사라집니다.')) return;
    
    setIsEditingMode(false);
    setEditingVersionName("");
    setEditingContent("");
  };

  // 새 버전 추가 기능
  const handleAddNewVersion = () => {
    if (isEditingMode) {
      alert('이미 편집 중인 버전이 있습니다. 먼저 현재 버전을 확정해주세요.');
      return;
    }
    
    const nextVersionNumber = (draft?.artifactVersions?.length || 0) + 1;
    const latestContent = confirmedVersions.length > 0 
      ? confirmedVersions[confirmedVersions.length - 1].content 
      : draft?.contentMd || '';
    
    setIsEditingMode(true);
    setEditingVersionName(`버전 ${nextVersionNumber}`);
    setEditingContent(latestContent);
  };

  const handleVersionNameDoubleClick = (version: DraftVersion) => {
    if (version.isLatest && version.id === "new" && isEditingMode) {
      setIsEditingVersionName(true);
      setTempVersionName(version.name);
    }
  };

  const handleVersionNameSave = () => {
    if (isEditingVersionName && tempVersionName.trim() && isEditingMode) {
      setEditingVersionName(tempVersionName.trim());
    }
    setIsEditingVersionName(false);
    setTempVersionName("");
  };

  const handleVersionNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleVersionNameSave();
    }
  };

  // 메모 저장 함수
  const handleSaveMemo = async () => {
    if (!draft?.draftId || !isMemoChanged) return;

    try {
      await updateDraftMeta({
        variables: {
          input: {
            draftId: draft.draftId,
            memo: draftMemo,
          }
        }
      });

      setIsMemoChanged(false);
      alert("메모가 저장되었습니다.");
    } catch (error) {
      console.error("Memo update failed:", error);
      alert("메모 저장에 실패했습니다. 다시 시도해주세요.");
    }
  };

  // 게시글 상태 새로고침
  const handleRefreshPlans = async () => {
    // refetchDraft를 우선 사용, 없으면 onRefreshDraft 사용
    const refreshFunction = refetchDraft || onRefreshDraft;
    if (!refreshFunction) return;

    setIsRefreshing(true);

    try {
      await refreshFunction();
      // 새로고침 완료 후 잠시 애니메이션 지속
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    } catch (error) {
      setIsRefreshing(false);
    }
  };

  // 원고 승인 (최신 버전으로 확정)
  const handleApproveDraft = async () => {
    if (!draft?.draftId) return;

    if (!window.confirm('현재 원고를 최신 버전으로 확정하시겠습니까? 확정 후에는 게시글 생성이 가능합니다.')) return;

    setIsApprovingDraft(true);

    try {
      const result = await approveDraft({
        variables: {
          input: {
            draftId: draft.draftId,
          }
        }
      });

      if (result.data?.approveDraft) {
        alert('원고가 승인되었습니다. 이제 게시글을 생성할 수 있습니다.');

        // draft 정보 다시 조회하여 상태 업데이트
        const refreshFunction = refetchDraft || onRefreshDraft;
        if (refreshFunction) {
          await refreshFunction();
        }
      }
    } catch (error) {
      console.error("Draft approval failed:", error);
      alert("원고 승인에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsApprovingDraft(false);
    }
  };

  // 원고 재오픈 (검토 상태로 돌아가기)
  const handleReopenDraft = async () => {
    if (!draft?.draftId) return;
    
    if (!window.confirm('원고를 검토 상태로 되돌리시겠습니까?')) return;
    
    try {
      const result = await reopenDraft({
        variables: {
          input: {
            draftId: draft.draftId,
          }
        }
      });
      
      if (result.data?.reopenDraft) {
        alert('원고가 검토 상태로 되돌아갔습니다.');
        
        // draft 정보 다시 조회하여 상태 업데이트
        const refreshFunction = refetchDraft || onRefreshDraft;
        if (refreshFunction) {
          refreshFunction();
        }
      }
    } catch (error) {
      console.error("Draft reopen failed:", error);
      alert("원고 재오픈에 실패했습니다. 다시 시도해주세요.");
    }
  };


  // draft 상태가 APPROVED가 아니면 선택된 채널들 초기화
  React.useEffect(() => {
    if (draft?.status !== 'APPROVED' || isApprovingDraft) {
      setSelectedChannels([]);
      setSelectAll(false);
    }
  }, [draft?.status, isApprovingDraft]);

  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      // draft가 APPROVED 상태이고 승인 중이 아닐 때만 선택 가능
      if (draft?.status !== 'APPROVED' || isApprovingDraft) {
        return;
      }
      // 선택 가능한 채널만 선택 (postId가 있는 완료 상태 제외)
      const selectableChannels = channelPosts
        .filter(post =>
          !((post.status === 'POST_CREATED' || post.status === 'COMPLETED') && post.postId) &&
          post.status !== 'PUBLISHED'
        )
        .map(post => post.id);
      setSelectedChannels(selectableChannels);
    } else {
      setSelectedChannels([]);
    }
  };

  const handleChannelSelect = (channelId: string, checked: boolean) => {
    // draft가 APPROVED 상태이고 승인 중이 아닐 때만 선택 가능
    if (draft?.status !== 'APPROVED' || isApprovingDraft) {
      return;
    }

    if (checked) {
      const newSelected = [...selectedChannels, channelId];
      setSelectedChannels(newSelected);
      // 선택 가능한 채널 수와 비교
      const selectableChannelsCount = channelPosts.filter(post =>
        !((post.status === 'POST_CREATED' || post.status === 'COMPLETED') && post.postId) &&
        post.status !== 'PUBLISHED'
      ).length;
      setSelectAll(newSelected.length === selectableChannelsCount);
    } else {
      const newSelected = selectedChannels.filter(id => id !== channelId);
      setSelectedChannels(newSelected);
      setSelectAll(false);
    }
  };

  // 선택된 채널들에 대해 게시글 생성 요청
  const handleCreatePosts = async () => {
    if (!draft?.draftId || !workflowTypeId || selectedChannels.length === 0) {
      alert('필요한 정보가 없습니다. 다시 시도해주세요.');
      return;
    }

    // 원고가 승인(APPROVED) 상태가 아니면 게시글 생성 불가
    if (draft.status !== 'APPROVED') {
      alert('원고를 먼저 승인해주세요. 승인된 원고만 게시글을 생성할 수 있습니다.');
      return;
    }

    // 편집 중인 버전이 있는 경우 게시글 생성 불가
    if (isEditingMode) {
      alert('현재 버전을 저장하거나 확정된 버전을 선택하세요.');
      return;
    }

    // 생성 중인 플랜이 있는지 확인
    const hasCreatingPlan = selectedChannels.some(channelId => {
      const plan = draft.plans?.find((p: any) => p.channelId === channelId);
      return plan && (plan.status === 'POST_REQUESTED' || plan.status === 'CREATING');
    });

    // 생성 중인 플랜이 있으면 확인 메시지 표시
    if (hasCreatingPlan) {
      if (!window.confirm('게시글 생성을 재요청하시겠습니까?')) {
        return;
      }
    }

    try {
      // 각 선택된 채널에 대해 순차적으로 처리
      for (const channelId of selectedChannels) {
        // 해당 채널의 기존 플랜 찾기
        const existingPlan = draft.plans?.find((plan: any) => plan.channelId === channelId);

        // 이미 생성완료된 상태(postId가 있는 경우)는 건너뜀
        if (existingPlan && (existingPlan.status === 'POST_CREATED' || existingPlan.status === 'COMPLETED') && existingPlan.postId) {
          continue;
        }

        if (existingPlan) {
          // 기존 플랜이 있으면 기존 planId로 requestPlanRendering 실행
          await requestPlanRendering({
            variables: {
              input: {
                draftId: draft.draftId,
                planId: existingPlan.planId,
              }
            }
          });
        } else {
          // 기존 플랜이 없으면 새 플랜 생성 후 즉시 requestPlanRendering 실행
          const createResult = await createPlan({
            variables: {
              input: {
                draftId: draft.draftId,
                channelId: channelId,
                workflowTypeId: workflowTypeId,
              }
            }
          });

          const newPlanId = createResult.data?.createPlan.planId;
          if (!newPlanId) {
            throw new Error(`채널 ${channelId}의 플랜 생성에 실패했습니다.`);
          }

          // 플랜 생성 즉시 request 실행
          await requestPlanRendering({
            variables: {
              input: {
                draftId: draft.draftId,
                planId: newPlanId,
              }
            }
          });
        }
      }

      alert(`${selectedChannels.length}개 채널에 게시글 생성 요청이 완료되었습니다.`);
      setSelectedChannels([]); // 선택 초기화
      setSelectAll(false); // 전체 선택 초기화

      // draft 정보 다시 조회하여 plans 업데이트
      const refreshFunction = refetchDraft || onRefreshDraft;
      if (refreshFunction) {
        refreshFunction();
      }

    } catch (error) {
      console.error('게시글 생성 요청 실패:', error);

      // GraphQL 오류인 경우 상세 정보 출력
      if (error && typeof error === 'object' && 'graphQLErrors' in error) {
        console.error('GraphQL Errors:', (error as any).graphQLErrors);
      }
      if (error && typeof error === 'object' && 'networkError' in error) {
        console.error('Network Error:', (error as any).networkError);
      }

      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
      console.error('Error message:', errorMessage);

      alert(`게시글 생성 요청에 실패했습니다.\n\n${errorMessage}\n\n다시 시도해주세요.`);
    }
  };

  // 개별 채널에 대해 게시글 재생성 (새 plan 생성)
  const handleRegeneratePost = async (channelId: string) => {
    if (!draft?.draftId || !workflowTypeId) {
      alert('필요한 정보가 없습니다. 다시 시도해주세요.');
      return;
    }

    if (!window.confirm('이 채널의 게시글을 재생성하시겠습니까?')) {
      return;
    }

    try {
      // 새 플랜 생성
      const createResult = await createPlan({
        variables: {
          input: {
            draftId: draft.draftId,
            channelId: channelId,
            workflowTypeId: workflowTypeId,
          }
        }
      });

      const newPlanId = createResult.data?.createPlan.planId;
      if (!newPlanId) {
        throw new Error('플랜 생성에 실패했습니다.');
      }

      // 플랜 생성 즉시 게시글 생성 요청
      await requestPlanRendering({
        variables: {
          input: {
            draftId: draft.draftId,
            planId: newPlanId,
          }
        }
      });

      alert('게시글 재생성 요청이 완료되었습니다.');

      // draft 정보 다시 조회하여 plans 업데이트
      const refreshFunction = refetchDraft || onRefreshDraft;
      if (refreshFunction) {
        refreshFunction();
      }

    } catch (error) {
      console.error('게시글 재생성 요청 실패:', error);

      if (error && typeof error === 'object' && 'graphQLErrors' in error) {
        console.error('GraphQL Errors:', (error as any).graphQLErrors);
      }
      if (error && typeof error === 'object' && 'networkError' in error) {
        console.error('Network Error:', (error as any).networkError);
      }

      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
      alert(`게시글 재생성 요청에 실패했습니다.\n\n${errorMessage}\n\n다시 시도해주세요.`);
    }
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'POST_FAILED':
      case 'POST_REQUEST_FAILED':
      case 'FAILED':
        return 'text-red-600 font-medium';
      case 'POST_REQUESTED':
      case 'CREATING':
        return 'text-blue-600 font-medium';
      case 'POST_CREATED':
      case 'COMPLETED':
        return 'text-green-600 font-medium';
      case 'PENDING':
      case 'READY_FOR_POST':
      default:
        return 'text-black font-medium';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return '생성대기';
      case 'READY_FOR_POST':
        return '생성대기';
      case 'POST_REQUESTED':
        return '생성중';
      case 'POST_FAILED':
        return '생성실패';
      case 'POST_REQUEST_FAILED':
        return '요청실패';
      case 'POST_CREATED':
        return '생성완료';
      case 'CREATING':
        return '게시글생성중';
      case 'FAILED':
        return '생성실패';
      case 'COMPLETED':
        return '생성완료';
      default:
        return status;
    }
  };

  // 비고 메시지 생성 함수 (또는 버튼 렌더링)
  const getMemoMessage = (post: any) => {
    const { status, memo, postId, id } = post;

    // 생성완료 상태면 게시글 보러가기 버튼과 재생성 버튼 반환
    if ((status === 'POST_CREATED' || status === 'COMPLETED') && postId) {
      return (
        <div className="flex gap-2 justify-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNavigateToPost(postId);
            }}
            className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
          >
            게시글 보러가기
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRegeneratePost(id);
            }}
            className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            게시글 재생성
          </button>
        </div>
      );
    }

    // statusDescription이 있으면 그것을 우선 표시
    if (memo && memo !== '-') {
      return memo;
    }

    // 생성 실패 상태인 경우
    if (status === 'POST_FAILED' || status === 'POST_REQUEST_FAILED' || status === 'FAILED') {
      return '게시글 생성에 실패했습니다. 다시 생성해보세요.';
    }

    return memo || '-';
  };

  const handleDeleteDraft = async () => {
    if (!draft) return;

    if (!window.confirm("정말로 이 원고를 삭제하시겠습니까?")) return;

    try {
      await deleteDraft({
        variables: {
          input: {
            draftId: draft.draftId,
          }
        }
      });

      alert("원고가 삭제되었습니다.");
      onClose(); // 삭제 후 목록으로 이동 (onClose가 refetch 포함)
    } catch (error) {
      console.error("Delete failed:", error);
      alert("원고 삭제에 실패했습니다.");
    }
  };

  // 모든 Hook 호출 이후에 early return 검사
  if (!draft) {
    return null;
  }

  return (
    <div className="w-full bg-white flex flex-col" style={{ height: '100vh' }}>
      {/* 헤더 */}
      <div className="border-b bg-white px-6 py-4 flex-shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="flex items-center text-gray-600 hover:text-gray-800"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <h1 className="text-xl font-bold">원고 상세</h1>
        </div>
      </div>

      {/* 스크롤 가능한 컨텐츠 영역 */}
      <div className="flex-1 overflow-y-auto">

      {/* 기본 정보 섹션 */}
      <div className="border-b bg-white px-6 py-6">
        <div className="space-y-4">
          {/* 상태 */}
          <div className="flex items-center">
            <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>상태</label>
            <span
              className={`w-32 rounded-full px-3 py-1 text-sm font-semibold border ${STATUS_COLORS[draft.status as keyof typeof STATUS_COLORS]} text-center inline-block`}
            >
              {STATUS_LABELS[draft.status as keyof typeof STATUS_LABELS]}
            </span>
          </div>

          {/* 제목 */}
          <div className="flex items-center">
            <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>제목</label>
            <p className="text-lg font-semibold text-gray-900">{draft.title}</p>
          </div>

          {/* 생성일시 */}
          <div className="flex items-center">
            <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>생성일시</label>
            <p className="text-sm text-gray-600">
              {formatDate(draft.createdAt, "YYYY년 MM월 DD일 HH:mm")}
            </p>
          </div>

          {/* 수정일시 */}
          <div className="flex items-center">
            <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>수정일시</label>
            <p className="text-sm text-gray-600">
              {formatDate(draft.updatedAt, "YYYY년 MM월 DD일 HH:mm")}
            </p>
          </div>

          {/* 최종 수정자 */}
          <div className="flex items-center">
            <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>최종 수정자</label>
            <p className="text-sm text-gray-600">{(draft as any).updatedBy || "-"}</p>
          </div>

          {/* 태그 */}
          <div className="flex items-center">
            <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>태그</label>
            <div className="flex flex-wrap gap-2">
              {draft.tags && draft.tags.length > 0 ? (
                draft.tags.map((tag: string, index: number) => (
                  <span
                    key={index}
                    className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 border border-blue-200"
                  >
                    {tag}
                  </span>
                ))
              ) : (
                <p className="text-sm text-gray-500">설정된 태그가 없습니다.</p>
              )}
            </div>
          </div>

          {/* 메모 */}
          <div className="flex">
            <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px', paddingTop: '8px' }}>메모</label>
            <div className="flex-1">
              <textarea
                value={draftMemo}
                onChange={(e) => {
                  setDraftMemo(e.target.value);
                  setIsMemoChanged(true);
                }}
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="메모를 입력하세요..."
              />
              <div className="mt-2 flex justify-end">
                <button
                  onClick={handleSaveMemo}
                  disabled={!isMemoChanged}
                  className={`px-3 py-1 text-sm rounded ${
                    isMemoChanged
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 메인 콘텐츠 - 버전 및 내용 */}
      <div className="mx-6 my-6">
        <div className="flex gap-6" style={{ height: '600px' }}>
          {/* 수정 버전내역 영역 */}
          <div className="w-1/4 flex flex-col" style={{ height: '600px' }}>
            <h3 className="font-medium text-gray-900 mb-4">수정 버전내역</h3>
            <div ref={versionListRef} className="flex-1 overflow-auto space-y-2" style={{ scrollbarWidth: 'thin' }}>
              {allVersions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-sm mb-4">아직 생성된 버전이 없습니다.</p>
                  <button
                    onClick={handleAddNewVersion}
                    className="px-4 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                  >
                    첫 번째 버전 만들기
                  </button>
                </div>
              ) : (
                allVersions.map((version) => (
                <button
                  key={version.id}
                  onClick={() => handleVersionSelect(version)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    selectedVersion?.id === version.id
                      ? 'bg-black text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <div className="font-medium text-sm">
                    {isEditingVersionName && version.id === "new" ? (
                      <input
                        type="text"
                        value={tempVersionName}
                        onChange={(e) => setTempVersionName(e.target.value)}
                        onBlur={handleVersionNameSave}
                        onKeyDown={handleVersionNameKeyDown}
                        className="w-full bg-white text-gray-900 border border-blue-300 outline-none rounded px-2 py-1"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <span 
                        onDoubleClick={() => handleVersionNameDoubleClick(version)}
                        className={version.isLatest ? "cursor-pointer underline" : ""}
                      >
                        {version.name}
                      </span>
                    )}
                  </div>
                  {version.confirmedAt && (
                    <div className={`text-xs mt-1 ${
                      selectedVersion?.id === version.id ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      {formatDate(version.confirmedAt, "MM.DD HH:mm")}
                    </div>
                  )}
                </button>
                ))
              )}
              
              {/* 새 버전 추가 버튼 - 편집 모드가 아니고 APPROVED 상태가 아닐 때만 보이기 */}
              {!isEditingMode && draft?.status !== 'APPROVED' && (
                <button
                  onClick={handleAddNewVersion}
                  className="w-full text-left p-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                >
                  <div className="text-center">
                    <div className="text-sm font-medium">+ 새 버전 추가</div>
                  </div>
                </button>
              )}
              
              {/* 편집 모드일 때 편집 버튼들을 버전 목록 안에 표시 */}
              {isEditingMode && (
                <div className="flex gap-1 mt-2">
                  <button
                    onClick={handleConfirmVersion}
                    className="flex-1 px-2 py-2 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                  >
                    저장
                  </button>
                  <button
                    onClick={handleReset}
                    className="flex-1 px-2 py-2 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50"
                  >
                    초기화
                  </button>
                  <button
                    onClick={handleCancelEditingVersion}
                    className="flex-1 px-2 py-2 border border-red-300 text-red-700 text-xs rounded hover:bg-red-50"
                  >
                    편집취소
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 내용 편집 영역 */}
          <div className="flex-1 flex flex-col" style={{ height: '600px' }}>
            <h3 className="font-medium text-gray-900 mb-4">내용</h3>
            {selectedVersion?.isLatest && isEditingMode ? (
              /* 편집 가능한 에디터 */
              <div 
                className="border rounded-md overflow-hidden"
                style={{ 
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '560px'
                }}
              >
                <div style={{ flex: 1, minHeight: 0 }}>
                  <Editor 
                    value={editingContent}
                    onChange={(value) => setEditingContent(value)}
                    height="100%"
                    mediaFolder="draft"
                  />
                </div>
              </div>
            ) : (
              /* 읽기 전용 내용 */
              <div className="flex flex-col">
                {confirmedVersions.length === 0 && !isEditingMode ? (
                  /* 버전이 없는 경우 기본 내용 보여주기 */
                  <div 
                    className="w-full border rounded-md p-6 bg-white overflow-auto"
                    style={{ height: '560px' }}
                  >
                    <div 
                      className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-em:text-gray-600 prose-ul:text-gray-700 prose-ol:text-gray-700"
                      dangerouslySetInnerHTML={{ __html: draft?.contentMd || '내용이 없습니다.' }}
                    />
                  </div>
                ) : (
                  /* 선택된 버전의 내용 보여주기 */
                  <div 
                    className="w-full border rounded-md p-6 bg-white overflow-auto"
                    style={{ height: '560px' }}
                  >
                    <div 
                      className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-em:text-gray-600 prose-ul:text-gray-700 prose-ol:text-gray-700"
                      dangerouslySetInnerHTML={{ __html: selectedVersion?.content || '' }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* 하단 버튼 영역 - 버전과 내용 하단에 같은 행으로 배치 */}
        <div className="flex gap-6 mt-4">
          {/* 버전 영역 하단 버튼들 */}
          <div className="w-1/4">
            {draft?.status === 'APPROVED' && (!draft?.plans || draft.plans.length === 0) ? (
              /* APPROVED 상태이고 plan이 없는 경우: 검토 상태로 돌아가기 버튼 */
              <button
                onClick={handleReopenDraft}
                disabled={isEditingMode}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  isEditingMode 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
              >
                <div className="text-center">
                  <div className="text-sm font-medium">검토 상태로 돌아가기</div>
                </div>
              </button>
            ) : draft?.status !== 'APPROVED' ? (
              /* APPROVED가 아닌 상태: 최신 버전으로 확정 버튼 */
              <button
                onClick={handleApproveDraft}
                disabled={isEditingMode}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  isEditingMode 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                <div className="text-center">
                  <div className="text-sm font-medium">최신 버전으로 확정</div>
                </div>
              </button>
            ) : null}
          </div>
          
          {/* 내용 영역 하단 버튼들 */}
          <div className="flex-1 flex justify-end gap-2">
            {!hasCompletedChannelPost && (
              <button 
                onClick={handleDeleteDraft}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                원고삭제
              </button>
            )}
            <button 
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              목록으로
            </button>
          </div>
        </div>
      </div>
      
      {/* 채널 게시글 테이블 */}
      <div className="border-t bg-white px-6 py-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-gray-900">게시글 생성</h3>
          <button
            onClick={handleRefreshPlans}
            disabled={isRefreshing}
            className={`flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 text-gray-500 hover:text-gray-700 hover:border-gray-400 transition-colors ${
              isRefreshing ? 'cursor-not-allowed opacity-75' : ''
            }`}
            title="게시글 상태 새로고침"
          >
            <ArrowPathIcon 
              className={`h-4 w-4 transition-transform duration-500 ${
                isRefreshing ? 'animate-spin' : ''
              }`} 
            />
          </button>
        </div>
        <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-black">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider" style={{ width: '80px' }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    disabled={draft?.status !== 'APPROVED' || isApprovingDraft}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                      draft?.status !== 'APPROVED' || isApprovingDraft ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                  />
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider" style={{ width: '180px' }}>
                  채널
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider" style={{ width: '200px' }}>
                  상태
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider" style={{ width: '200px' }}>
                  요청일시
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider" style={{ minWidth: '520px' }}>
                  비고
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {channelPosts.map((post) => {
                const isSelected = selectedChannels.includes(post.id);
                return (
                  <tr 
                    key={post.id} 
                    className={`cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-center" style={{ width: '80px' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={draft?.status !== 'APPROVED' ||
                                  isApprovingDraft ||
                                  ((post.status === 'POST_CREATED' || post.status === 'COMPLETED') && !!post.postId) ||
                                  post.status === 'PUBLISHED'}
                        onChange={(e) => handleChannelSelect(post.id, e.target.checked)}
                        className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                          draft?.status !== 'APPROVED' ||
                          isApprovingDraft ||
                          ((post.status === 'POST_CREATED' || post.status === 'COMPLETED') && !!post.postId) ||
                          post.status === 'PUBLISHED' ? 'cursor-not-allowed opacity-50' : ''
                        }`}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center" style={{ width: '180px' }}>
                      <div className="flex justify-center">
                        <span className="text-sm font-medium text-gray-900">
                          {post.channel}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center" style={{ width: '200px' }}>
                      <span className={`text-sm ${getStatusColor(post.status)}`}>
                        {getStatusLabel(post.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-500" style={{ width: '200px' }}>
                      {post.requestedAt && post.requestedAt !== '-'
                        ? formatDate(post.requestedAt, "YYYY-MM-DD HH:mm:ss")
                        : '-'
                      }
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 text-center" style={{ minWidth: '520px' }}>
                      {getMemoMessage(post)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* 테이블 하단 버튼들 */}
        <div className="flex justify-between items-center mt-6 mb-16">
          <div>
            <button
              onClick={handleCreatePosts}
              disabled={draft?.status !== 'APPROVED' || isApprovingDraft || selectedChannels.length === 0 || !workflowTypeId}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              선택 채널 게시글 생성
            </button>
          </div>
        </div>
      </div>

      </div> {/* 스크롤 가능한 컨텐츠 영역 종료 */}
    </div>
  );
}